import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import {
  adminApi,
  type PromotionalGrantRow,
  type SubscriptionEventRow,
  type SubscriptionMovement,
  type SubscriptionOverview,
} from '../lib/api.js';
import { useAsync, useAction } from '../lib/useAsync.js';
import { useCan } from '../lib/capabilities.js';
import { count, numeric } from '../lib/format.js';
import { missingGranterLabel } from '../lib/grants.js';
import { TabNav, type RecordTabs } from '../components/Record.js';
import { InfoBubble, Screen } from '../components/Screen.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { Select } from '../components/Select.js';
import {
  AsyncBoundary,
  Button,
  DotPill,
  EmptyState,
  Field,
  HoldButton,
  Note,
  OutcomeNote,
  Panel,
  PanelBody,
  Table,
  Td,
  When,
  inputStyle,
} from '../components/ui.js';

/**
 * Subscriptions — RidgeWill/org-admin#162.
 *
 * Three screens over auth's subscription records, for Support, Auth and Global
 * Admin (`subscription:read`):
 *
 *   Overview  what is active now, what changed week by week, what needs
 *             attention, and whether RevenueCat is still talking to us
 *   Grants    every promotional grant, filterable, exportable, endable
 *   Activity  every subscription change, newest first
 *
 * ── THE HISTORY STARTS WHEN auth#540 SHIPPED ─────────────────────────
 *
 * Every chart and list of changes reads auth's subscription history, which did
 * not exist before 2026-09-28. The Overview says when it starts, so an empty
 * early chart reads as "no history yet" rather than "nothing happened".
 */
type Section = 'overview' | 'grants' | 'activity';

const TABS: RecordTabs<Section> = [
  { path: 'overview', label: 'Overview' },
  { path: 'grants', label: 'Promotional grants' },
  { path: 'activity', label: 'Activity' },
];

export function Subscriptions({ section }: { section?: string }) {
  const mayRead = useCan('subscription:read');
  if (!mayRead) {
    return (
      <Screen>
        <Note>Your role does not include subscriptions. Support, Auth and Global Admin can read them.</Note>
      </Screen>
    );
  }
  const current: Section = TABS.find((t) => t.path === section)?.path ?? 'overview';
  const tabs = <TabNav base="/subscriptions" tabs={TABS} current={current} label="Subscriptions sections" />;
  if (current === 'grants') return <Grants tabs={tabs} />;
  if (current === 'activity') return <Activity tabs={tabs} />;
  return <Overview tabs={tabs} />;
}

// ── Shared vocabulary ────────────────────────────────────────────────

const STORE_NAMES: Record<string, string> = {
  APP_STORE: 'App Store',
  MAC_APP_STORE: 'Mac App Store',
  PLAY_STORE: 'Google Play',
  AMAZON: 'Amazon',
  STRIPE: 'Stripe',
  RC_BILLING: 'Web (RevenueCat)',
  PADDLE: 'Paddle',
  ROKU: 'Roku',
  PROMOTIONAL: 'RevenueCat promotional',
  TEST_STORE: 'Test Store',
  PROMOTIONAL_GRANT: 'Promotional grant',
  UNKNOWN: 'Not recorded',
};
const storeName = (s: string) => STORE_NAMES[s] ?? s;

/** Names are free text (auth#567): one in capitals reads as "Premium", anything else as typed. */
function tierLabel(name: string): string {
  if (name === 'FREE') return 'Free';
  return name === name.toUpperCase() ? name.charAt(0) + name.slice(1).toLowerCase() : name;
}

const words = (s: string | null) => (s ? s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ') : '');

const MOVEMENT_LABEL: Record<SubscriptionMovement, string> = {
  new: 'New purchases',
  granted: 'Grants',
  renewed: 'Renewals',
  lapsed: 'Lapsed',
  refunded: 'Refunded',
  grantEnded: 'Grants ended',
};

const muted = { color: C.textMid, fontSize: '.8rem' } as const;

/** `source` says why a grant has no granter; see lib/grants. */
function PersonLink({ person, source }: { person: { id: string; email: string } | null; source?: string | null }) {
  if (!person) return <span style={muted}>{missingGranterLabel(source)}</span>;
  return <a href={`#/users/${encodeURIComponent(person.id)}`}>{person.email}</a>;
}

function Tile({ label, value, note, warn }: { label: string; value: string; note?: string; warn?: boolean }) {
  return (
    <div style={{ ...s.tile, ...(warn ? { borderColor: C.warnDot } : {}) }}>
      <span style={s.tileLabel}>{label}</span>
      <span style={{ ...s.tileValue, ...numeric, color: warn ? C.warnText : C.text }}>{value}</span>
      {note ? <span style={s.tileNote}>{note}</span> : null}
    </div>
  );
}

// ── Overview ─────────────────────────────────────────────────────────

function Overview({ tabs }: { tabs: ReactNode }) {
  const { state, reload } = useAsync(() => adminApi.subscriptionOverview(), []);
  return (
    <Screen filters={tabs} actions={<Button onClick={() => reload()}>Refresh</Button>}>
      <AsyncBoundary state={state}>{(d) => <OverviewBody d={d} />}</AsyncBoundary>
    </Screen>
  );
}

function OverviewBody({ d }: { d: SubscriptionOverview }) {
  const { active, attention, sync } = d;
  const attentionCount =
    attention.paymentRetrying.length + attention.grantsEnding.length + attention.renewalOverdue.length;

  return (
    <>
      <Panel title="Active now">
        <div style={s.tiles}>
          <Tile label="Active" value={count(active.total)} />
          <Tile label="Paid" value={count(active.paid)} />
          <Tile label="Promotional" value={count(active.promotional)} />
          <Tile
            label="Payment retrying"
            value={count(active.paymentRetrying)}
            warn={active.paymentRetrying > 0}
            note={active.paymentRetrying > 0 ? 'Still entitled while the store retries.' : undefined}
          />
        </div>
      </Panel>

      <Movements weeks={d.weeks} historySince={d.historySince} />

      <Panel
        title="Active by application"
        info={
          <InfoBubble label="What counts as active">
            Paid subscriptions that are active or in a billing retry, and promotional grants still in
            force. A grant past its end date is not counted, even before the daily job closes it.
          </InfoBubble>
        }
      >
        {active.breakdown.length === 0 ? (
          <EmptyState title="Nothing active" suggestion="Paid subscriptions and grants in force appear here." />
        ) : (
          <Table columns={['Application', 'Tier', 'Source', 'Active']}>
            {active.breakdown.map((b) => (
              <tr key={`${b.appSlug}|${b.tier}|${b.source}`}>
                <Td>{b.appName}</Td>
                <Td>{tierLabel(b.tier)}</Td>
                <Td>{storeName(b.source)}</Td>
                <Td>
                  <span style={numeric}>{count(b.count)}</span>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      <Panel
        title={attentionCount > 0 ? `Needs attention (${count(attentionCount)})` : 'Needs attention'}
        info={
          <InfoBubble label="What is listed here">
            Paying people whose renewal failed and the store is still retrying; promotional grants ending in
            the next 30 days; and purchases still active past the date they were paid through, which means a
            store update never arrived (the daily RevenueCat check corrects these). Purchases whose owner
            switched off auto-renew cannot be listed: the store keeps them active until the date, and nothing
            changes on our side until then.
          </InfoBubble>
        }
      >
        {attentionCount === 0 ? (
          <EmptyState title="Nothing needs attention" />
        ) : (
          <Table columns={['Person', 'Application', 'Tier', 'Why', 'When']}>
            {attention.paymentRetrying.map((r) => (
              <tr key={`retry-${r.user.id}-${r.application.slug}`}>
                <Td>
                  <PersonLink person={r.user} />
                </Td>
                <Td>{r.application.name}</Td>
                <Td>{tierLabel(r.tier)}</Td>
                <Td>
                  <DotPill tone="warn">Payment retrying</DotPill>
                </Td>
                <Td>{r.currentPeriodEnd ? <>Retrying until <When iso={r.currentPeriodEnd} /></> : '-'}</Td>
              </tr>
            ))}
            {attention.grantsEnding.map((r) => (
              <tr key={`ending-${r.user.id}-${r.application.slug}`}>
                <Td>
                  <PersonLink person={r.user} />
                </Td>
                <Td>{r.application.name}</Td>
                <Td>{tierLabel(r.tier)}</Td>
                <Td>
                  <DotPill tone="muted">Grant ending</DotPill>
                  {r.grantReason ? <div style={muted}>{r.grantReason}</div> : null}
                </Td>
                <Td>{r.currentPeriodEnd ? <>Ends <When iso={r.currentPeriodEnd} /></> : '-'}</Td>
              </tr>
            ))}
            {attention.renewalOverdue.map((r) => (
              <tr key={`overdue-${r.user.id}-${r.application.slug}`}>
                <Td>
                  <PersonLink person={r.user} />
                </Td>
                <Td>{r.application.name}</Td>
                <Td>{tierLabel(r.tier)}</Td>
                <Td>
                  <DotPill tone="warn">No renewal received</DotPill>
                </Td>
                <Td>{r.currentPeriodEnd ? <>Paid through <When iso={r.currentPeriodEnd} /></> : '-'}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      <SyncHealth sync={sync} />
    </>
  );
}

function SyncHealth({ sync }: { sync: SubscriptionOverview['sync'] }) {
  const job = (label: string, run: SubscriptionOverview['sync']['lastLapseSweep']) => (
    <tr>
      <Td>{label}</Td>
      <Td>
        {run ? (
          <DotPill tone={run.ok ? 'good' : 'danger'}>{run.ok ? 'Succeeded' : 'Failed'}</DotPill>
        ) : (
          <DotPill tone="muted">No run recorded</DotPill>
        )}
      </Td>
      <Td>{run ? <When iso={run.at} /> : '-'}</Td>
    </tr>
  );
  return (
    <Panel
      title="Sync with RevenueCat"
      info={
        <InfoBubble label="Why this is here">
          RevenueCat tells us about purchases, renewals and expiries. If it stops, tiers go stale with no error
          anywhere: nobody is downgraded and nobody new is upgraded. The daily check asks RevenueCat directly
          about anything that should have renewed and corrects it; it needs the RevenueCat API key to run.
        </InfoBubble>
      }
    >
      {!sync.reconciliationConfigured ? (
        <PanelBody>
          <Note tone="warn">
            The daily RevenueCat check is not configured in this environment, so a lost store update is never
            corrected.
          </Note>
        </PanelBody>
      ) : null}
      <Table columns={['', 'State', 'When']}>
        <tr>
          <Td>Last update from RevenueCat</Td>
          <Td>
            <span style={numeric}>{count(sync.webhooksLast24h)}</span> in the last 24 hours
          </Td>
          <Td>{sync.lastWebhookAt ? <When iso={sync.lastWebhookAt} /> : 'Never'}</Td>
        </tr>
        {job('Daily RevenueCat check', sync.lastLapseSweep)}
        {job('Grant end dates closed', sync.lastGrantExpiry)}
      </Table>
    </Panel>
  );
}

// ── The movements chart ─────────────────────────────────────────────

/**
 * Week by week: gains above the line, losses below it.
 *
 * THREE SERIES, NOT SIX. Above: new purchases and grants. Below: everything
 * that ended entitlement (lapsed, refunded, grants ended), as one. Renewals are
 * not a change in how many people hold a tier, so they are in the tooltip and
 * the table rather than the bars. Six stacked colours would be unreadable at
 * this size; the tooltip and the table carry the full breakdown.
 *
 * COLOURS: the dataviz reference palette's first three categorical slots, the
 * only set that validates for every pair, checked against this portal's light
 * (#FFFFFF) and dark (#131A28) surfaces with the skill's validator. The aqua
 * sits at 2.82:1 on white, so the legend and the table view are required, not
 * optional. The brand's status colours are deliberately not used: they mean
 * good and bad elsewhere in the portal, and "a grant ended" is not a failure.
 */
const SERIES = [
  { key: 'new', label: 'New purchases', color: 'var(--subs-new)', side: 1 },
  { key: 'granted', label: 'Grants', color: 'var(--subs-granted)', side: 1 },
  { key: 'ended', label: 'Ended', color: 'var(--subs-ended)', side: -1 },
] as const;

const CHART_CSS = `
.subs-chart { --subs-new: #2a78d6; --subs-granted: #1baf7a; --subs-ended: #eb6834; }
@media (prefers-color-scheme: dark) {
  :root:where(:not([data-theme="light"])) .subs-chart { --subs-new: #3987e5; --subs-granted: #199e70; --subs-ended: #d95926; }
}
:root[data-theme="dark"] .subs-chart { --subs-new: #3987e5; --subs-granted: #199e70; --subs-ended: #d95926; }
`;

type Week = SubscriptionOverview['weeks'][number];
const endedOf = (w: Week) => w.counts.lapsed + w.counts.refunded + w.counts.grantEnded;
const valueOf = (w: Week, key: (typeof SERIES)[number]['key']) => (key === 'ended' ? endedOf(w) : w.counts[key]);

const weekFmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });

function useWidth<T extends HTMLElement>(fallback = 720) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => entry && setWidth(Math.max(320, entry.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

function niceMax(v: number): number {
  if (v <= 4) return 4;
  const step = 10 ** Math.floor(Math.log10(v));
  const n = Math.ceil(v / step);
  return (n <= 2 ? 2 : n <= 5 ? 5 : 10) * step;
}

function Movements({ weeks, historySince }: { weeks: Week[]; historySince: string | null }) {
  const [asTable, setAsTable] = useState(false);
  const total = weeks.reduce(
    (sum, w) => sum + w.counts.new + w.counts.granted + w.counts.renewed + endedOf(w),
    0,
  );
  return (
    <Panel
      title="Changes by week"
      info={
        <InfoBubble label="What the chart shows">
          New purchases and new grants above the line; subscriptions that ended below it (lapsed, refunded,
          or a grant ended). Renewals do not change how many people hold a tier, so they are in the tooltip
          and the table. Weeks start on Monday (UTC).
          {historySince ? (
            <>
              {' '}
              History starts <When iso={historySince} />; nothing earlier was recorded.
            </>
          ) : null}
        </InfoBubble>
      }
      action={
        <Button onClick={() => setAsTable((v) => !v)}>{asTable ? 'Show chart' : 'Show as table'}</Button>
      }
    >
      <style>{CHART_CSS}</style>
      {total === 0 ? (
        <EmptyState
          title="No changes recorded in these twelve weeks"
          suggestion={historySince ? undefined : 'The history starts with the first purchase, grant or lapse.'}
        />
      ) : asTable ? (
        <Table columns={['Week of', ...Object.values(MOVEMENT_LABEL)]}>
          {[...weeks].reverse().map((w) => (
            <tr key={w.week}>
              <Td>{weekFmt.format(new Date(w.week))}</Td>
              {(Object.keys(MOVEMENT_LABEL) as SubscriptionMovement[]).map((m) => (
                <Td key={m}>
                  <span style={numeric}>{count(w.counts[m])}</span>
                </Td>
              ))}
            </tr>
          ))}
        </Table>
      ) : (
        <MovementsChart weeks={weeks} />
      )}
    </Panel>
  );
}

const M = { top: 12, right: 12, bottom: 26, left: 40 };
const PLOT_H = 200;

function MovementsChart({ weeks }: { weeks: Week[] }) {
  const { ref, width } = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const up = Math.max(0, ...weeks.map((w) => w.counts.new + w.counts.granted));
  const down = Math.max(0, ...weeks.map(endedOf));
  const top = niceMax(up);
  const bottom = down > 0 ? niceMax(down) : 0;
  const span = top + bottom;
  const plotW = width - M.left - M.right;
  const band = plotW / Math.max(1, weeks.length);
  const barW = Math.max(4, Math.min(28, band - 6));
  const y = (v: number) => M.top + ((top - v) / span) * PLOT_H;
  const H = M.top + PLOT_H + M.bottom;
  // A midpoint only when it is a whole number: "2.5 subscriptions" is not a count.
  const ticks = useMemo(() => {
    const half = (v: number) => (Number.isInteger(v / 2) ? [v / 2] : []);
    const out = [top, ...half(top), 0];
    if (bottom > 0) out.push(...half(bottom).map((v) => -v), -bottom);
    return out;
  }, [top, bottom]);
  const hovered = hover === null ? null : weeks[hover];

  return (
    <div ref={ref} className="subs-chart" style={{ position: 'relative', padding: '.4rem 1.1rem 0' }} onPointerLeave={() => setHover(null)}>
      <Legend />
      <svg viewBox={`0 0 ${width} ${H}`} width="100%" height={H} role="group" aria-label="Subscription changes by week">
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={M.left}
              x2={width - M.right}
              y1={y(t)}
              y2={y(t)}
              stroke={t === 0 ? C.muted : C.border}
              strokeWidth={1}
            />
            <text x={M.left - 8} y={y(t)} dy="0.32em" textAnchor="end" fontSize={10} fill={C.muted} style={numeric}>
              {count(Math.abs(t))}
            </text>
          </g>
        ))}
        {weeks.map((w, i) => {
          const cx = M.left + i * band + band / 2;
          let upBase = 0;
          const segs: ReactNode[] = [];
          for (const series of SERIES.filter((sr) => sr.side === 1)) {
            const v = valueOf(w, series.key);
            if (v <= 0) continue;
            const yTop = y(upBase + v);
            const h = Math.max(1, y(upBase) - yTop - (upBase > 0 ? 2 : 0));
            segs.push(<rect key={series.key} x={cx - barW / 2} y={yTop} width={barW} height={h} rx={2} fill={series.color} />);
            upBase += v;
          }
          const ended = endedOf(w);
          if (ended > 0) {
            const h = Math.max(1, y(-ended) - y(0));
            segs.push(<rect key="ended" x={cx - barW / 2} y={y(0) + 1} width={barW} height={h} rx={2} fill="var(--subs-ended)" />);
          }
          const labelEvery = Math.ceil(weeks.length / Math.max(1, Math.floor(plotW / 64)));
          return (
            <g key={w.week}>
              {hover === i ? <rect x={cx - band / 2} y={M.top} width={band} height={PLOT_H} fill={C.border3} /> : null}
              {segs}
              {i % labelEvery === 0 ? (
                <text x={cx} y={H - 8} textAnchor="middle" fontSize={10} fill={C.muted}>
                  {weekFmt.format(new Date(w.week))}
                </text>
              ) : null}
              <rect
                x={cx - band / 2}
                y={M.top}
                width={band}
                height={PLOT_H}
                fill="transparent"
                tabIndex={0}
                aria-label={`Week of ${weekFmt.format(new Date(w.week))}: ${w.counts.new} new, ${w.counts.granted} granted, ${ended} ended, ${w.counts.renewed} renewed`}
                style={{ outline: 'none' }}
                onPointerMove={() => setHover(i)}
                onFocus={() => setHover(i)}
                onBlur={() => setHover(null)}
              />
            </g>
          );
        })}
      </svg>
      {hovered && hover !== null ? (
        <div
          role="tooltip"
          style={{
            ...s.tip,
            // BESIDE the week, never over it: a tooltip that covers the bar it
            // describes hides the thing being read. Right of the band when it
            // fits, otherwise left of it.
            left: (() => {
              const x = 16 + M.left + hover * band; // the band's left edge, in the container
              return x + band + 8 + 180 <= width ? x + band + 8 : Math.max(0, x - 8 - 180);
            })(),
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '.3rem' }}>Week of {weekFmt.format(new Date(hovered.week))}</div>
          {(Object.keys(MOVEMENT_LABEL) as SubscriptionMovement[]).map((m) => (
            <div key={m} style={s.tipRow}>
              <span>{MOVEMENT_LABEL[m]}</span>
              <span style={numeric}>{count(hovered.counts[m])}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Legend() {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem 1rem', fontSize: '.8rem', color: C.textMid, marginBottom: '.3rem' }}>
      {SERIES.map((sr) => (
        <span key={sr.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem' }}>
          <span aria-hidden style={{ width: 10, height: 10, borderRadius: 2, background: sr.color }} />
          {sr.label}
          {sr.side === -1 ? ' (below the line)' : ''}
        </span>
      ))}
    </div>
  );
}

// ── Promotional grants ───────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'active', label: 'In force' },
  { value: 'ended', label: 'Ended' },
  { value: 'all', label: 'All grants' },
] as const;

function Grants({ tabs }: { tabs: ReactNode }) {
  const [status, setStatus] = useState<'active' | 'ended' | 'all'>('active');
  const [app, setApp] = useState('');
  const [endingSoon, setEndingSoon] = useState(false);
  const [ending, setEnding] = useState<PromotionalGrantRow | null>(null);
  const { state, reload } = useAsync(() => adminApi.subscriptionGrants({ status }), [status]);

  return (
    <Screen filters={tabs} actions={<Button onClick={() => reload()}>Refresh</Button>}>
      <AsyncBoundary state={state}>
        {({ grants }) => {
          // App and "ending soon" narrow client-side, so the app list stays whole while filtering.
          const apps = [...new Map(grants.map((g) => [g.application.slug, g.application.name])).entries()].sort(
            (a, b) => a[1].localeCompare(b[1]),
          );
          const soon = Date.now() + 30 * 86_400_000;
          const shown = grants.filter(
            (g) =>
              (!app || g.application.slug === app) &&
              (!endingSoon || (g.inForce && g.currentPeriodEnd !== null && new Date(g.currentPeriodEnd).getTime() <= soon)),
          );
          return (
            <Panel
              title={`Promotional grants (${count(shown.length)})`}
              info={
                <InfoBubble label="Who can end a grant">
                  Auth Admin and Global Admin can end any grant. A Support Admin can end the grants they made.
                  The End button appears only where you can.
                </InfoBubble>
              }
              action={
                <Button disabled={shown.length === 0} onClick={() => downloadCsv(shown)}>
                  Export CSV
                </Button>
              }
            >
              <PanelBody>
                <div style={s.filters}>
                  <Select value={status} options={STATUS_OPTIONS} onChange={setStatus} label="Status" size="sm" />
                  <Select
                    value={app}
                    options={[{ value: '', label: 'All applications' }, ...apps.map(([value, label]) => ({ value, label }))]}
                    onChange={setApp}
                    label="Application"
                    size="sm"
                  />
                  {status !== 'ended' ? (
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem', fontSize: '.85rem' }}>
                      <input type="checkbox" checked={endingSoon} onChange={(e) => setEndingSoon(e.target.checked)} />
                      Ending in the next 30 days
                    </label>
                  ) : null}
                </div>
              </PanelBody>
              {shown.length === 0 ? (
                <EmptyState title="No grants match" />
              ) : (
                <Table columns={['Person', 'Application', 'Tier', 'Granted by', 'Reason', 'Ends', '']} actionsLast>
                  {shown.map((g) => (
                    <tr key={`${g.user.id}-${g.application.slug}`}>
                      <Td top>
                        <PersonLink person={g.user} />
                      </Td>
                      <Td top>{g.application.name}</Td>
                      <Td top>
                        {tierLabel(g.tier)}
                        {!g.inForce ? <div style={muted}>Ended</div> : null}
                      </Td>
                      <Td top>
                        <PersonLink person={g.grantedBy} source={g.grantSource} />
                        <div style={muted}>
                          <When iso={g.grantedAt} />
                        </div>
                      </Td>
                      <Td top>{g.grantReason ?? ''}</Td>
                      <Td top>{g.currentPeriodEnd ? <When iso={g.currentPeriodEnd} /> : 'No end date'}</Td>
                      <Td action top>
                        {g.canEnd ? (
                          <Button tone="danger" onClick={() => setEnding(g)}>
                            End
                          </Button>
                        ) : null}
                      </Td>
                    </tr>
                  ))}
                </Table>
              )}
              {ending ? (
                <EndGrant
                  grant={ending}
                  onClose={() => setEnding(null)}
                  onDone={() => {
                    setEnding(null);
                    reload();
                  }}
                />
              ) : null}
            </Panel>
          );
        }}
      </AsyncBoundary>
    </Screen>
  );
}

/** A spreadsheet cell: quoted, inner quotes doubled, and a leading =+-@ defused so no sheet runs it as a formula. */
function csvCell(v: string): string {
  const safe = /^[=+\-@]/.test(v) ? `'${v}` : v;
  return `"${safe.replace(/"/g, '""')}"`;
}

function downloadCsv(grants: PromotionalGrantRow[]) {
  const header = ['Person', 'Application', 'Tier', 'In force', 'Granted', 'Granted by', 'Reason', 'Ends'];
  const rows = grants.map((g) => [
    g.user.email,
    g.application.name,
    g.tier,
    g.inForce ? 'yes' : 'no',
    g.grantedAt,
    g.grantedBy?.email ?? missingGranterLabel(g.grantSource),
    g.grantReason ?? '',
    g.currentPeriodEnd ?? '',
  ]);
  const text = [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `promotional-grants-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function EndGrant({ grant, onClose, onDone }: { grant: PromotionalGrantRow; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const action = useAction();
  return (
    <Modal
      title={`End ${grant.user.email}'s ${grant.application.name} grant`}
      subtitle={`They drop to Free on ${grant.application.name} straight away.`}
      onClose={onClose}
      width={480}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <HoldButton
            title="Hold for a second and a half to end this grant."
            disabled={action.running || reason.trim().length < 3}
            onHold={() =>
              void action
                .run(async () => {
                  await adminApi.endSubscriptionGrant(grant.user.id, {
                    appSlug: grant.application.slug,
                    reason: reason.trim(),
                  });
                  return 'Grant ended.';
                })
                .then(onDone)
            }
          >
            {action.running ? 'Ending…' : 'End grant'}
          </HoldButton>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />
      <Field label="Reason" hint="Required. Recorded with the change.">
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          autoFocus
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </Field>
    </Modal>
  );
}

// ── Activity ─────────────────────────────────────────────────────────

const SOURCE_OPTIONS = [
  { value: '', label: 'Every cause' },
  { value: 'webhook', label: 'Store events' },
  { value: 'reconcile', label: 'RevenueCat corrections' },
  { value: 'grant', label: 'Granted by staff' },
  { value: 'grant-ended', label: 'Grants ended by staff' },
  { value: 'grant-expired', label: 'Grants reaching their end date' },
] as const;

function cause(e: SubscriptionEventRow): string {
  switch (e.source) {
    case 'grant':
      return 'Granted by staff';
    case 'grant-ended':
      return 'Grant ended by staff';
    case 'grant-expired':
      return 'Grant reached its end date';
    case 'reconcile':
      return 'Corrected from RevenueCat';
    case 'webhook':
      return e.eventType ? words(e.eventType) : 'Store event';
    default:
      return e.source;
  }
}

const tierAndStatus = (tier: string | null, status: string | null) =>
  tier === null ? 'Nothing' : `${tierLabel(tier)}, ${words(status)}`;

function Activity({ tabs }: { tabs: ReactNode }) {
  const [source, setSource] = useState('');
  const [pages, setPages] = useState<SubscriptionEventRow[][]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<string | null>(null);

  const load = async (before?: string) => {
    setLoading(true);
    setFailure(null);
    try {
      const page = await adminApi.subscriptionEvents({ source: source || undefined, before });
      setPages((prev) => (before ? [...prev, page.events] : [page.events]));
      setNext(page.next);
    } catch (err) {
      setFailure(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // `load` reads `source`; re-run from the top whenever the filter changes.
  }, [source]);

  const events = pages.flat();

  return (
    <Screen filters={tabs} actions={<Button onClick={() => void load()}>Refresh</Button>}>
      <Panel
        title="Subscription activity"
        info={
          <InfoBubble label="What is recorded">
            Every change to a tier, its status or its end date: store events, corrections from RevenueCat,
            and staff grants. A cancellation that is still paid through changes nothing until its date, so it
            appears when the subscription actually ends.
          </InfoBubble>
        }
      >
        <PanelBody>
          <div style={s.filters}>
            <Select value={source} options={SOURCE_OPTIONS} onChange={setSource} label="Cause" size="sm" />
          </div>
        </PanelBody>
        {failure ? (
          <PanelBody>
            <Note tone="danger">{failure}</Note>
          </PanelBody>
        ) : null}
        {events.length === 0 && !loading ? (
          <EmptyState title="No subscription changes recorded" />
        ) : (
          <Table columns={['When', 'Person', 'Application', 'Change', 'Cause', 'By', 'Reason']}>
            {events.map((e) => (
              <tr key={e.id}>
                <Td top>
                  <When iso={e.occurredAt} />
                </Td>
                <Td top>
                  <PersonLink person={e.user} />
                </Td>
                <Td top>{e.application.name}</Td>
                <Td top>
                  {tierAndStatus(e.fromTier, e.fromStatus)} → {tierAndStatus(e.toTier, e.toStatus)}
                </Td>
                <Td top>
                  {cause(e)}
                  {e.store ? <div style={muted}>{storeName(e.store)}</div> : null}
                </Td>
                <Td top>{e.actor ? <PersonLink person={e.actor} /> : <span style={muted}>System</span>}</Td>
                <Td top>{e.reason ?? ''}</Td>
              </tr>
            ))}
          </Table>
        )}
        {next || loading ? (
          <PanelBody>
            <Button disabled={loading} onClick={() => next && void load(next)}>
              {loading ? 'Loading…' : 'Load more'}
            </Button>
          </PanelBody>
        ) : null}
      </Panel>
    </Screen>
  );
}

const s = {
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '.75rem',
    padding: '1rem 1.1rem',
  },
  tile: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '.15rem',
    padding: '.7rem .85rem .75rem',
    background: C.page,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
  },
  tileLabel: { fontSize: '.78rem', color: C.textMid },
  tileValue: { fontSize: '1.45rem', fontWeight: 700 },
  tileNote: { fontSize: '.75rem', color: C.textMid },
  filters: { display: 'flex', flexWrap: 'wrap' as const, gap: '.6rem', alignItems: 'center' },
  tip: {
    position: 'absolute' as const,
    top: 36,
    width: 180,
    padding: '.55rem .7rem',
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    boxShadow: '0 6px 20px rgba(0,0,0,.18)',
    fontSize: '.8rem',
    color: C.text,
    pointerEvents: 'none' as const,
    zIndex: 5,
  },
  tipRow: { display: 'flex', justifyContent: 'space-between', gap: '.75rem', color: C.textMid },
};
